import { useEffect, useMemo, useState } from "react";
import {
  useCreateVoucher,
  useNextVoucherNumber,
  useOneVoucher,
  useUpdateVoucher,
} from "http-hooks/voucher/useVoucher";
import { useUploadDocument } from "http-hooks/document/useDocument";
import { useAllPartySupMasts } from "http-hooks/partysupmast/usepartysupmast";
import { useAllBanks } from "http-hooks/bank/useBank";
import { useAllBankPayTps } from "http-hooks/bankpaytp/useBankPayTp";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import TextArea from "components/ui/TextArea";
import FileUploader from "components/ui/FileUploader";
import Modal from "components/ui/modals";
import useCompanyPreferences from "hooks/useCompanyPreferences";

const VOUCHER_CONFIG = {
  CC: { title: "Cash Receipt", settlementLabel: "Cash Account", receipt: true },
  CD: { title: "Cash Payment", settlementLabel: "Cash Account", receipt: false },
  BC: { title: "Bank Receipt", settlementLabel: "Bank Account", receipt: true },
  BD: { title: "Bank Payment", settlementLabel: "Bank Account", receipt: false },
};

const isBankAccount = (account) => account.ps_Id.startsWith("BANK");

const getCashBankDocFilename = (voucherType, docno) => {
  if (!voucherType || !docno) return "";
  return `VCH-${voucherType}-${docno}`;
};

export default function SetCashBankVoucher({ id, onClose, open, type }) {
  const config = VOUCHER_CONFIG[type];
  const { data: existingVoucher, isLoading: isLoadingVoucher } = useOneVoucher(id);
  const { data: voucherNumberData } = useNextVoucherNumber(type, !id);
  const { data: accountsResponse, isLoading: isLoadingAccounts } = useAllPartySupMasts({
    level: 0,
    grplevel: 0,
    ag_status: 1,
    noPagination: true,
  });
  const { data: bankPaymentTypesResponse, isLoading: isLoadingBankPaymentTypes } =
    useAllBankPayTps();
  const { data: banksResponse, isLoading: isLoadingBanks } = useAllBanks();
  const { mutateAsync: createVoucher, isPending: isCreating } = useCreateVoucher();
  const { mutateAsync: updateVoucher, isPending: isUpdating } = useUpdateVoucher();
  const { mutateAsync: uploadDocument, isPending: isUploading } = useUploadDocument();
  const { isDocUploaderEnabled } = useCompanyPreferences();
  const [selectedSettlementAccount, setSelectedSettlementAccount] = useState(null);
  const [selectedCounterpartAccount, setSelectedCounterpartAccount] = useState(null);
  const [selectedExternalBank, setSelectedExternalBank] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [formData, setFormData] = useState({
    docno: "",
    trdate: "",
    tramount: "",
    naration: "",
    docmode: "Transfer",
    bankname: "",
    trnid: "",
    trndt: "",
  });

  const accounts = useMemo(() => accountsResponse?.data || [], [accountsResponse]);
  const bankPaymentTypes = useMemo(
    () => bankPaymentTypesResponse?.data || [],
    [bankPaymentTypesResponse]
  );
  const banks = useMemo(() => banksResponse?.data || [], [banksResponse]);
  const settlementAccounts = useMemo(() => {
    if (type === "CC" || type === "CD") {
      return accounts.filter((account) => account.ps_Id === "CASHAC");
    }
    return accounts.filter(isBankAccount);
  }, [accounts, type]);
  // Allow cash/bank on the other side for contra transfers (bank↔bank, cash↔bank).
  // Exclude only the selected settlement account so both sides can't be the same ledger.
  const counterpartAccounts = useMemo(() => {
    const settlementId = selectedSettlementAccount?.ps_Id;
    return accounts.filter((account) => account.ps_Id !== settlementId);
  }, [accounts, selectedSettlementAccount]);

  useEffect(() => {
    if (!id && voucherNumberData?.nextVoucherNumber) {
      setFormData((current) => ({
        ...current,
        docno: voucherNumberData.nextVoucherNumber,
      }));
    }
  }, [id, voucherNumberData]);

  useEffect(() => {
    if ((type === "CC" || type === "CD") && settlementAccounts.length === 1) {
      setSelectedSettlementAccount(settlementAccounts[0]);
    }
  }, [settlementAccounts, type]);

  // Clear counterpart if it matches the settlement account after a change.
  useEffect(() => {
    if (
      selectedSettlementAccount &&
      selectedCounterpartAccount &&
      selectedSettlementAccount.ps_Id === selectedCounterpartAccount.ps_Id
    ) {
      setSelectedCounterpartAccount(null);
    }
  }, [selectedSettlementAccount, selectedCounterpartAccount]);

  useEffect(() => {
    if (!existingVoucher || !id) return;

    const settlementId = config.receipt
      ? existingVoucher.acdebit
      : existingVoucher.accredit;
    const counterpartId = config.receipt
      ? existingVoucher.accredit
      : existingVoucher.acdebit;
    setSelectedSettlementAccount(
      accounts.find((account) => account.ps_Id === settlementId) || null
    );
    setSelectedCounterpartAccount(
      accounts.find((account) => account.ps_Id === counterpartId) || null
    );
    setSelectedExternalBank(
      banks.find((bank) => bank.bankname === existingVoucher.bankname) || null
    );
    setFormData({
      docno: existingVoucher.docno || "",
      trdate: existingVoucher.trdate || "",
      tramount: existingVoucher.tramount || "",
      naration: existingVoucher.naration || "",
      docmode: existingVoucher.docmode || "Transfer",
      bankname: existingVoucher.bankname || "",
      trnid: existingVoucher.trnid || "",
      trndt: existingVoucher.trndt || "",
    });
  }, [accounts, banks, config.receipt, existingVoucher, id]);

  const resolvedVoucherType = type || existingVoucher?.trtyp;

  const docFilename = useMemo(
    () => getCashBankDocFilename(resolvedVoucherType, formData.docno),
    [resolvedVoucherType, formData.docno]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedSettlementAccount || !selectedCounterpartAccount) return;

    const payload = {
      ...formData,
      trtyp: type,
      acdebit: config.receipt
        ? selectedSettlementAccount.ps_Id
        : selectedCounterpartAccount.ps_Id,
      accredit: config.receipt
        ? selectedCounterpartAccount.ps_Id
        : selectedSettlementAccount.ps_Id,
      bankname: type === "BC" ? selectedExternalBank?.bankname || "" : "",
      trndt: formData.trndt || "",
    };

    try {
      const savedVoucher = id
        ? await updateVoucher({ id, data: payload })
        : await createVoucher(payload);
      const savedDocumentNumber = savedVoucher?.docno || payload.docno;
      const voucherType = type || savedVoucher?.trtyp || payload.trtyp;
      const uploadFilename = getCashBankDocFilename(
        voucherType,
        savedDocumentNumber
      );
      if (attachment && uploadFilename) {
        await uploadDocument({
          file: attachment,
          filename: uploadFilename,
        });
      }
      onClose();
    } catch (error) {
      console.error("Voucher save failed:", error);
    }
  };

  if (!config) return null;
  const isBankVoucher = type === "BC" || type === "BD";
  const isPending = isCreating || isUpdating || isUploading;

  return (
    <Modal
      open={open}
      setOpen={(isOpen) => !isOpen && onClose()}
      maxWidth="4xl"
      header={<h3 className="text-base font-semibold leading-6 text-gray-900">{id ? "Update" : "Add"} {config.title}</h3>}
      footer={
        <div className="flex justify-end gap-x-3">
          <button type="button" className="rounded-md px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-100" onClick={onClose} disabled={isPending}>
            Cancel
          </button>
          <button type="submit" form="cash-bank-voucher-form" className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-500 disabled:cursor-not-allowed disabled:opacity-50" disabled={isPending}>
            {isPending ? "Saving..." : "Save"}
          </button>
        </div>
      }
    >
      {isLoadingVoucher || isLoadingAccounts || isLoadingBankPaymentTypes || isLoadingBanks ? (
        <div className="animate-pulse space-y-4">
          <div className="h-10 rounded bg-gray-200" />
          <div className="h-10 rounded bg-gray-200" />
          <div className="h-10 rounded bg-gray-200" />
        </div>
      ) : (
        <form id="cash-bank-voucher-form" onSubmit={handleSubmit}>
          <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <Input label="Voucher No." value={formData.docno} readOnly />
              {!id && (
                <p className="mt-1 text-xs text-gray-500">
                  {voucherNumberData?.previousVoucherNumber
                    ? `Previous Voucher No.: ${voucherNumberData.previousVoucherNumber}`
                    : "No previous voucher in this financial year."}
                </p>
              )}
            </div>
            <Input label="Date *" type="date" name="trdate" value={formData.trdate} onChange={handleChange} now={!id} withinFY required />
            <Select
              label={`${config.settlementLabel} *`}
              options={settlementAccounts}
              value={selectedSettlementAccount}
              onChange={setSelectedSettlementAccount}
              getOptionValue={(account) => account.ps_Id}
              getOptionLabel={(account) => account.name}
              isSearchable
              isClearable={false}
              isDisabled={type === "CC" || type === "CD"}
            />
          </div>

          <div className="mb-5 sm:flex">
            <div className="sm:w-2/3 sm:border-r sm:border-gray-200 sm:pr-6">
              <h3 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-700">Particulars</h3>
              <div className="mb-4">
                <Select
                  label={`${config.receipt ? "Credit" : "Debit"} A/c *`}
                  options={counterpartAccounts}
                  value={selectedCounterpartAccount}
                  onChange={setSelectedCounterpartAccount}
                  getOptionValue={(account) => account.ps_Id}
                  getOptionLabel={(account) => account.name}
                  placeholder="Select here"
                  isSearchable
                  isClearable
                />
              </div>
              <TextArea label="Narration" name="naration" value={formData.naration} onChange={handleChange} placeholder="Narration" rows={3} maxLength={255} />
            </div>
            <div className="mt-4 sm:mt-0 sm:w-1/3 sm:pl-6">
              <h3 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-700">Amount</h3>
              <Input type="number" name="tramount" value={formData.tramount} onChange={handleChange} placeholder="0.00" min={0.01} step="0.01" showLabel={false} required />
            </div>
          </div>

          {isBankVoucher && (
            <div className="mb-5">
              <h3 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-700">Bank Details</h3>
              <div className={`grid grid-cols-1 gap-4 ${type === "BC" ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
                {type === "BC" && (
                  <Select
                    label="Name of Bank"
                    options={banks}
                    value={selectedExternalBank}
                    onChange={setSelectedExternalBank}
                    getOptionValue={(bank) => bank.bnkid}
                    getOptionLabel={(bank) => bank.bankname}
                    placeholder="Select here"
                    isClearable
                    isSearchable
                  />
                )}
                <Select
                  label="Payment Mode"
                  options={bankPaymentTypes}
                  value={bankPaymentTypes.find(
                    (paymentType) => paymentType.paytype === formData.docmode
                  ) || null}
                  onChange={(paymentType) =>
                    setFormData((current) => ({
                      ...current,
                      docmode: paymentType?.paytype || "",
                    }))
                  }
                  getOptionValue={(paymentType) => paymentType.paytyp}
                  getOptionLabel={(paymentType) => paymentType.paytype}
                  placeholder="Select here"
                  isClearable
                  isSearchable
                />
                <Input label="Transaction / Cheque No." name="trnid" value={formData.trnid} onChange={handleChange} maxLength={25} />
                <Input label="Transaction Date" type="date" name="trndt" value={formData.trndt} onChange={handleChange} withinFY />
              </div>
            </div>
          )}

          {isDocUploaderEnabled() && (docFilename || !id) && (
            <div className="mb-5">
              <FileUploader
                label="Attachment"
                docFilename={id ? docFilename : ""}
                deferred
                selectedFile={attachment}
                onFileSelected={setAttachment}
              />
            </div>
          )}
        </form>
      )}
    </Modal>
  );
}
